import { useState } from 'react'
import {
  BuildingListItem,
  ModelsImagableType1 as ImagableType,
} from '@/shared/api/topli/TopliApi.ts'
import { CreateBuildingModal } from './Modals/CreateBuildingModal.tsx'
import { EditBuildingModal } from './Modals/EditBuildingModal.tsx'
import { BuildingsTable } from './BuildingTable/BuildingsTable.tsx'
import { Button } from '@/shared/ui/Button/Button.tsx'
import { PlusCircleIcon } from 'lucide-react'
import { DeleteBuildingAlert } from '@/features/Building/DeleteBuildingAlert'
import { FullTextDescriptionModal } from '@/shared/ui/ModalDialogs'
import { EditUploadedPhotosModal, PhotoOwnerCaption, PhotosCarousel } from '@/entities/Photos'
import { EditPhotoCropModal } from '@/features/Photos/EditPhotoCropModal'
import { buildingsByPageQueryKey, currentBuildingByIdQueryKey } from '@/entities/Building'
import { DeletePhotoAlert } from '@/features/Photos/DeletePhotoAlert'
import { UploadPhotosForm } from '@/features/Photos/UploadPhotosForm'
import { useTranslation } from 'react-i18next'
import { getCurrentTranslation } from '@/shared/lib/utils.ts'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store.ts'
import { usePhotosOrderState } from '@/shared/lib/usePhotosOrderState'

export const BuildingsView = () => {
  const { language } = useLanguageStore()
  const [currentTable, setCurrentTable] = useState<BuildingListItem | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [showMoreDialogOpen, setShowMoreDialogOpen] = useState(false)
  const [showPhotosDialogOpen, setPhotosDialogOpen] = useState(false)
  const [openUploadedPhotosDialog, setOpenUploadedPhotosDialog] = useState(false)
  const [openEditCropPhotoDialog, setOpenEditCropPhotoDialog] = useState(false)
  const [openDeletePhotoDialog, setOpenDeletePhotoDialog] = useState(false)
  const [selectPhotoId, setSelectedPhotoId] = useState<number | null>(null)
  const {
    handleCheckPhotosOrderChanged: handleBuildingPhotosOrderChanged,
    handleInvalidateIfChanged: invalidateBuildingPhotos,
  } = usePhotosOrderState({
    queryKey: buildingsByPageQueryKey,
  })
  const { t } = useTranslation(['building'])

  const handleCloseModal = () => {
    setCurrentTable(null)
    setEditDialogOpen(false)
    setCreateDialogOpen(false)
    setPhotosDialogOpen(false)
  }

  const handleOpenEditModal = (building: BuildingListItem) => {
    setCurrentTable(building)
    setEditDialogOpen(true)
  }

  const handleOpenDeleteModal = (building: BuildingListItem) => {
    setCurrentTable(building)
    setDeleteDialogOpen(true)
  }

  const handleOpenShowMoreModal = (building: BuildingListItem) => {
    const translatedDescription = getCurrentTranslation(building, language, 'description')
    setCurrentTable({
      ...building,
      description: translatedDescription,
    })
    setShowMoreDialogOpen(true)
  }

  const handleOpenPhotos = (building: BuildingListItem) => {
    setCurrentTable(building)
    setPhotosDialogOpen(true)
  }

  const handleOpenUploadedPhotos = (building: BuildingListItem) => {
    setCurrentTable(building)
    setOpenUploadedPhotosDialog(true)
  }

  const handleOpenEditCropPhoto = (photoId: number) => {
    setSelectedPhotoId(photoId)
    setOpenEditCropPhotoDialog(true)
  }

  const handleDeletePhoto = (photoId: number) => {
    setSelectedPhotoId(photoId)
    setOpenDeletePhotoDialog(true)
  }

  const handleCloseCropModal = () => {
    setSelectedPhotoId(null)
    setOpenEditCropPhotoDialog(false)
  }

  const handleCloseUploadedPhotosModal = (isOpen: boolean) => {
    setOpenUploadedPhotosDialog(isOpen)

    if (!isOpen) {
      invalidateBuildingPhotos()
    }
  }

  return (
    <>
      <div className='flex flex-col md:w-full w-[calc(100%+2.5rem)] md:mx-0 mx-[-1.25rem] bg-white rounded-[1.25rem] shadow-navbar p-4'>
        <BuildingsTable
          onOpenEditModal={handleOpenEditModal}
          onOpenDeleteModal={handleOpenDeleteModal}
          onOpenFullDescription={handleOpenShowMoreModal}
          onOpenPhotos={handleOpenPhotos}
          onOpenUploadedPhotos={handleOpenUploadedPhotos}
          addButtonSlot={
            <Button
              variant='primary'
              color='base'
              size='md'
              onClick={() => setCreateDialogOpen(true)}
              leftIcon={<PlusCircleIcon size={16} />}
            >
              {t('building:buttons.new_building')}
            </Button>
          }
        />
      </div>
      <CreateBuildingModal
        dialogProps={{ open: createDialogOpen, onOpenChange: setCreateDialogOpen }}
        onCloseModal={handleCloseModal}
      />
      {currentTable && (
        <EditBuildingModal
          dialogProps={{ open: editDialogOpen, onOpenChange: setEditDialogOpen }}
          onCloseModal={handleCloseModal}
          currentBuildingId={currentTable.id}
        />
      )}
      {currentTable && showPhotosDialogOpen && (
        <PhotosCarousel
          isOpened={showPhotosDialogOpen}
          obj_id={currentTable?.id}
          owner={PhotoOwnerCaption.Building}
          setIsOpened={setPhotosDialogOpen}
          onCloseModal={handleCloseModal}
          photos={currentTable?.photos || []}
        />
      )}
      {showMoreDialogOpen && (
        <FullTextDescriptionModal
          dialogProps={{ open: showMoreDialogOpen, onOpenChange: setShowMoreDialogOpen }}
          description={currentTable?.description}
        />
      )}
      {selectPhotoId && openDeletePhotoDialog && (
        <DeletePhotoAlert
          open={openDeletePhotoDialog}
          onOpenChange={setOpenDeletePhotoDialog}
          selectPhotoId={selectPhotoId}
          queryKeyForUpdate={buildingsByPageQueryKey}
          // queryKeyForUpdate={PhotoOwnerCaption.Unit}
          onCloseModal={handleCloseCropModal}
        />
      )}
      {currentTable && selectPhotoId && (
        <EditPhotoCropModal
          dialogProps={{
            open: openEditCropPhotoDialog,
            onOpenChange: setOpenEditCropPhotoDialog,
          }}
          onCloseModal={handleCloseCropModal}
          selectedPhotoId={selectPhotoId}
          queryKeyForObjectUpdate={currentBuildingByIdQueryKey}
          queryKeyForPageUpdate={buildingsByPageQueryKey}
          obj_id={currentTable.id}
          owner={PhotoOwnerCaption.Building}
        />
      )}
      {currentTable && (
        <EditUploadedPhotosModal
          dialogProps={{
            open: openUploadedPhotosDialog,
            onOpenChange: value => {
              handleCloseUploadedPhotosModal(value)
            },
          }}
          obj_id={currentTable.id}
          owner={PhotoOwnerCaption.Building}
          uploadDropzoneSlot={
            <UploadPhotosForm
              obj_id={currentTable.id}
              ownerValue={ImagableType.Building}
              queryKeyForUpdate={buildingsByPageQueryKey}
            />
          }
          onDeletePhoto={handleDeletePhoto}
          onOpenCropPhoto={handleOpenEditCropPhoto}
          handleCheckPhotosOrderChanged={handleBuildingPhotosOrderChanged}
        />
      )}
      {currentTable && (
        <DeleteBuildingAlert
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          selectBuildingId={currentTable.id}
          onClose={handleCloseModal}
        />
      )}
    </>
  )
}
